import { useCallback, useEffect, useRef } from "react";

import { closest } from "./DomUtils";
import { useNavigateTo } from "../Routes/RoutesContext";
import { useIsLibraryFileUrl } from "./libraryLinks";

const ExternalLinksManager = props => {
  const { children } = props;

  const isLibraryFileUrl = useIsLibraryFileUrl();

  const navigateTo = useNavigateTo();

  const isSupportedLinkNode = useCallback(el => {
    // If the node has no `href` attribute, or has an `src` attribute (IE 11 <img> tags
    // have both `src` and `href` attributes), this is not a link.
    if (!el.href || el.src) return false;

    // Checks whether the provided HTML node object is a link that opens in a new tab or
    // forces same tab native navigation. This allows :
    //
    //   - target="_blank" links to be actually opened in a new tab
    //   - target="_self" links to be opened in a new browser HTTP request, and not as
    //     a route change in react-router.
    //
    if (opensInNewTab(el) || forcesSameTabNativeNavigation(el)) return true;

    // If the link is an anchor link for the same location, we don't want to handle it
    // with the router.
    const currentLocation = window.location.href.split("#")[0];
    const linkAnchor = el.href.split(currentLocation)[1];
    if (linkAnchor?.match(/^#/)) return false;

    // If the link uses another protocol than HTTP or HTTPS and is not a path (which
    // starts with a "/"), we don't want to let the browser handle it.
    if (!el.href.match(/^(https?:\/\/|\/)/i)) return false;

    return true;
  }, []);

  const isLibraryFileLink = useCallback(
    el => isLibraryFileUrl(el?.href),
    [isLibraryFileUrl]
  );

  const handleClick = useCallback(
    e => {
      // Find the closest parent element that is a supported link node
      const link = closest(e.target, isSupportedLinkNode);

      // If no link was found we delegate the click event to the browser
      if (!link) return;

      e.preventDefault();
      e.stopPropagation();

      if (forcesSameTabNativeNavigation(link)) {
        window.location.href = link.href;
      } else if (opensInNewTab(link) || isLibraryFileLink(link)) {
        window.open(link.href, "_blank");
      } else {
        navigateTo(link.href);
      }
    },
    [isSupportedLinkNode, isLibraryFileLink, navigateTo]
  );

  // Keep the latest handler in a ref and register a single stable listener on mount,
  // instead of re-running this effect (removing then re-adding the body listener) every
  // time navigateTo's identity changes (e.g. on every route change, since it depends on
  // the current location). Churning the listener like that leaves a brief window where
  // a real click can land while it's detached, silently swallowing the navigation.
  const handleClickRef = useRef(handleClick);
  handleClickRef.current = handleClick;

  useEffect(() => {
    const listener = e => handleClickRef.current(e);

    document.body.addEventListener("click", listener);

    return () => document.body.removeEventListener("click", listener);
  }, []);

  return children;
};

function opensInNewTab(el) {
  const target = el.getAttribute("target");
  return el.href && target === "_blank";
}

function forcesSameTabNativeNavigation(el) {
  const target = el.getAttribute("target");
  return el.href && (target === "_self" || target === "_top");
}

export default ExternalLinksManager;
